import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Part, PartSortOrQuery } from 'src/app/model/station-logs/part';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { TabService } from 'src/app/core/services/tab.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { StationLogApiService } from 'src/app/services/api/station-log-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { PartsDialogComponent } from '../../components/parts-dialog/parts-dialog.component';
import { PARTS_SEARCH_TABLE_DEFINITION } from '../../definitions/parts-search-table-definition';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { PartsTableService } from 'src/app/services/table/parts-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata } from '@openng/optimus-ui/api';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { ButtonModule } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-parts-search-container',
  imports: [
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslateModule,
    DynamicDialogModule,
    BlockUIModule,
    ButtonModule,
  ],
  templateUrl: './parts-search-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parts-search-container.component.scss',
})
export class PartsSearchContainerComponent
  extends SearchContainerComponent<Part>
  implements OnInit, OnDestroy
{
  protected _partsTableService = inject(PartsTableService);
  protected _translateService = inject(TranslateService);
  protected _authApi = inject(AuthApiService);
  protected _stationLogApi = inject(StationLogApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _dialogService = inject(DialogService);
  protected _tabService = inject(TabService);

  ref: DynamicDialogRef<PartsDialogComponent> | null = null;

  pageLoading = signal(true);
  parts: WritableSignal<Part[]> = signal([]);
  canCreate = signal(false);

  activeTabChangedSub!: Subscription;

  ngOnInit(): void {
    this._partsTableService.setLastLazyLoad(null);
    this.setTableService(this._partsTableService);
    this.columns = PARTS_SEARCH_TABLE_DEFINITION;

    const screenName$ = this._translateService.get(
      'OPMONITORING.SCREEN.PARTS_SEARCH'
    );
    forkJoin([screenName$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
    });

    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.PARTS_SEARCH.CREATE_PARTS
      )
    );

    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._partsTableService.setLastLazyLoad($event.lazy);
    const query: PartSortOrQuery = {
      sapNumber: undefined,
      pciNumber: undefined,
      description: undefined,
      vendorPartNumber: undefined,
      manufacturer: undefined,
      storeroom: undefined,
      binLocation: undefined,
    };

    if (filters.sapNumber) {
      query.sapNumber = (filters.sapNumber as FilterMetadata).value;
    }
    if (filters.pciNumber) {
      query.pciNumber = (filters.pciNumber as FilterMetadata).value;
    }
    if (filters.description) {
      query.description = (filters.description as FilterMetadata).value;
    }
    if (filters.vendorPartNumber) {
      query.vendorPartNumber = (
        filters.vendorPartNumber as FilterMetadata
      ).value;
    }
    if (filters.manufacturer) {
      query.manufacturer = (filters.manufacturer as FilterMetadata).value;
    }
    if (filters.storeroom) {
      query.storeroom = (filters.storeroom as FilterMetadata).value;
    }
    if (filters.binLocation) {
      query.binLocation = (filters.binLocation as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'sapNumber');
    this.pageLoading.set(true);
    this._stationLogApi
      .queryForParts(req.page, req.pageSize, query, sort)
      .subscribe({
        next: (response) => {
          this.parts.set(response.items);
          this.totalRecords = response.totalRecords;
          this.elements = [...this.parts()];
          this.pageLoading.set(false);
        },
        error: (error) => {
          this.pageLoading.set(false);
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.QUERY_PARTS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  async changeViewEvent($event: { data: Part }): Promise<void> {
    const dialogEditHeader = await firstValueFrom(
      this._translateService.get('OPMONITORING.MESSAGES.HEADERS.EDIT_PART')
    );
    this.ref = this._dialogService.open(PartsDialogComponent, {
      header: dialogEditHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: {
        part: $event.data,
        formType: 'Edit',
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the part
        // so we don't need to refresh the page
        return;
      }
      this.refresh();
    });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._partsTableService.getLastLazyLoad()! });
  }

  async newPart(): Promise<void> {
    const dialogCreateHeader = await firstValueFrom(
      this._translateService.get('OPMONITORING.MESSAGES.HEADERS.CREATE_PART')
    );
    this.ref = this._dialogService.open(PartsDialogComponent, {
      header: dialogCreateHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: {
        part: null,
        formType: 'Create',
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the part
        // so we don't need to refresh the page
        return;
      }
      this.refresh();
    });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
